import React from 'react';
import PropTypes from 'prop-types';

/**
 * Sparkline Component
 * Dependency-free SVG sparkline: a polyline with an optional translucent area fill.
 * No axes, no labels, no event handlers.
 *
 * Behavior (locked by the queue-monitor v1 contract, section 6.3):
 * - `points` empty/undefined -> muted "no data yet" placeholder of identical dimensions
 *   (no layout shift).
 * - All-equal values (including all-zero) -> a horizontal midline.
 * - Single point -> a midline dot.
 *
 * @param {number[]} points - y-values, oldest-first; nulls filtered out by caller
 * @param {number} width - rendered width in px (default 120)
 * @param {number} height - rendered height in px (default 32)
 * @param {string} stroke - line color (default '#0d6efd')
 * @param {bool} fill - translucent area under the polyline (default true)
 * @param {string} ariaLabel - accessible label (default 'trend sparkline')
 */
const PADDING = 2;

const round2 = (n) => Math.round(n * 100) / 100;

const Sparkline = ({ points, width, height, stroke, fill, ariaLabel }) => {
	// Defensive: callers filter nulls per contract, but never let a stray
	// non-finite value produce NaN coordinates in the SVG.
	const values = Array.isArray(points)
		? points.filter((v) => typeof v === 'number' && Number.isFinite(v))
		: [];

	if (values.length === 0) {
		return (
			<div
				className="sparkline sparkline-empty text-muted"
				style={{
					width: `${width}px`,
					height: `${height}px`,
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center',
					fontSize: '0.7rem',
					lineHeight: 1,
				}}
				role="img"
				aria-label={ariaLabel}
			>
				no data yet
			</div>
		);
	}

	const min = Math.min(...values);
	const max = Math.max(...values);
	const range = max - min;
	const innerWidth = width - PADDING * 2;
	const innerHeight = height - PADDING * 2;

	const toX = (index) =>
		values.length === 1
			? width / 2
			: PADDING + (index / (values.length - 1)) * innerWidth;
	// Flat series (range 0, including all-zero) renders as a horizontal midline.
	const toY = (value) =>
		range === 0
			? height / 2
			: PADDING + (1 - (value - min) / range) * innerHeight;

	if (values.length === 1) {
		return (
			<svg
				className="sparkline"
				width={width}
				height={height}
				viewBox={`0 0 ${width} ${height}`}
				role="img"
				aria-label={ariaLabel}
			>
				<circle
					className="sparkline-dot"
					cx={round2(toX(0))}
					cy={round2(toY(values[0]))}
					r={2}
					fill={stroke}
				/>
			</svg>
		);
	}

	const linePoints = values
		.map((value, index) => `${round2(toX(index))},${round2(toY(value))}`)
		.join(' ');
	// No area for a flat series: filling from the midline down would imply
	// magnitude that is not in the data.
	const showArea = fill && range > 0;
	const areaPoints = showArea
		? `${linePoints} ${round2(toX(values.length - 1))},${height - PADDING} ${round2(toX(0))},${height - PADDING}`
		: null;

	return (
		<svg
			className="sparkline"
			width={width}
			height={height}
			viewBox={`0 0 ${width} ${height}`}
			role="img"
			aria-label={ariaLabel}
		>
			{showArea && (
				<polygon
					className="sparkline-area"
					points={areaPoints}
					fill={stroke}
					fillOpacity={0.15}
					stroke="none"
				/>
			)}
			<polyline
				className="sparkline-line"
				points={linePoints}
				fill="none"
				stroke={stroke}
				strokeWidth={1.5}
				strokeLinejoin="round"
				strokeLinecap="round"
			/>
		</svg>
	);
};

Sparkline.propTypes = {
	points: PropTypes.arrayOf(PropTypes.number),
	width: PropTypes.number,
	height: PropTypes.number,
	stroke: PropTypes.string,
	fill: PropTypes.bool,
	ariaLabel: PropTypes.string,
};

Sparkline.defaultProps = {
	points: undefined,
	width: 120,
	height: 32,
	stroke: '#0d6efd',
	fill: true,
	ariaLabel: 'trend sparkline',
};

export default Sparkline;
